import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n';
import {
  ExternalLink, ArrowLeft, Server,
  Shield, Zap, HardDrive, Database, CreditCard, Network, Cpu, Monitor,
} from 'lucide-react';
import { getRootserverBySlug, ROOTSERVER_TYPES } from '@/data/rootserver';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, breadcrumbSchema } from '@/lib/seo';

const featureIcons = [Cpu, HardDrive, Shield, Database, Zap, Network, CreditCard, Monitor];

function PlanCard({ plan, rs, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: plan.soldOut ? 0.6 : 1, y: 0 }}
      className={`relative flex flex-col rounded-2xl border p-6 ${
        plan.popular
          ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
          : 'border-border glass'
      }`}
      data-testid={`rs-plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-xs px-3 py-1 font-bold uppercase tracking-wider">
            {t('rootserver.bestSeller')}
          </Badge>
        </div>
      )}

      <div className="mb-2">
        <h3 className="text-lg font-bold">{plan.name}</h3>
        <p className="text-xs text-muted-foreground">{t(`rootserver.${rs.tPrefix}_plan${plan._idx}_sub`)}</p>
      </div>

      <div className="mb-6 mt-2">
        {plan.originalPrice && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm text-red-500 line-through font-medium">€{plan.originalPrice}</span>
            <span className="text-xs font-bold text-green-500 bg-green-500/10 px-1.5 py-0.5 rounded-full">
              -{Math.round((1 - parseFloat(plan.price) / parseFloat(plan.originalPrice)) * 100)}%
            </span>
          </div>
        )}
        <div className="flex items-baseline gap-1">
          <span className={`text-4xl font-black ${plan.originalPrice ? 'text-green-400' : 'text-primary'}`}>€{plan.price}</span>
          <span className="text-sm text-muted-foreground">{t('common.perMonth')}</span>
        </div>
      </div>

      <ul className="space-y-2.5 mb-8 flex-1">
        {plan.specs.map((spec) => (
          <li key={spec.labelKey} className="flex items-center justify-between text-sm gap-2">
            <span className="text-muted-foreground shrink-0">{t(`vps.spec_${spec.labelKey}`)}</span>
            <span className="font-medium text-right leading-tight">{spec.value}</span>
          </li>
        ))}
      </ul>

      {plan.soldOut ? (
        <Button
          disabled
          className="w-full font-bold bg-secondary text-secondary-foreground"
          data-testid={`order-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
        >
          {t('rootserver.soldOut')}
        </Button>
      ) : (
        <a href={plan.orderUrl} target="_blank" rel="noopener noreferrer">
          <Button
            className={`w-full font-bold ${
              plan.popular
                ? 'bg-primary hover:bg-primary/90 glow-hover'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            }`}
            data-testid={`order-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
          >
            {t('rootserver.configureNow')}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>
      )}
    </motion.div>
  );
}

export default function RootserverDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const rs = getRootserverBySlug(slug);

  if (!rs) {
    return (
      <Layout>
        <div className="section-spacing flex flex-col items-center justify-center text-center">
          <Server className="w-16 h-16 text-muted-foreground mb-6" />
          <h1 className="text-3xl font-black mb-4">Rootserver not found</h1>
          <Link to="/rootserver">
            <Button className="bg-primary hover:bg-primary/90">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('rootserver.backToAll')}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const featureNums = Array.from({ length: rs.numFeatures }, (_, i) => i + 1);
  const plansWithIdx = rs.plans.map((p, i) => ({ ...p, _idx: i + 1 }));

  const cols =
    plansWithIdx.length >= 4 ? 'sm:grid-cols-2 lg:grid-cols-3'
    : plansWithIdx.length === 3 ? 'md:grid-cols-3'
    : 'md:grid-cols-2 max-w-2xl mx-auto';

  return (
    <Layout>
      <SEOHead
        title={`${t(`rootserver.${rs.tPrefix}_title`)} from €${rs.fromPrice}/month`}
        description={t(`rootserver.${rs.tPrefix}_meta`)}
        canonical={`/rootserver/${rs.slug}`}
        schema={[
          productSchema({
            name: t(`rootserver.${rs.tPrefix}_title`),
            description: t(`rootserver.${rs.tPrefix}_meta`),
            path: `/rootserver/${rs.slug}`,
            image: rs.penguinImage,
            offers: rs.plans.map((plan) => ({ name: plan.name, price: plan.price, period: 'month', soldOut: plan.soldOut })),
          }),
          breadcrumbSchema([
            { name: 'Root Server', path: '/rootserver' },
            { name: t(`rootserver.${rs.tPrefix}_title`), path: `/rootserver/${rs.slug}` },
          ]),
        ]}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Back */}
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
            <Link
              to="/rootserver"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-rootserver"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('rootserver.backToAll')}
            </Link>
          </motion.div>

          {/* ── Two-column Hero ─────────────────────────────────────────────── */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex flex-wrap gap-2 mb-4">
                {rs.badgeKey && (
                  <Badge className="bg-primary text-xs uppercase tracking-wider">
                    {t(`rootserver.${rs.badgeKey}`)}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">{t('rootserver.prepaid')}</Badge>
                <Badge variant="outline" className="text-xs">{t('rootserver.noContract')}</Badge>
              </div>

              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                {t(`rootserver.${rs.tPrefix}_hero1`)}<br />
                <span className="text-gradient">{t(`rootserver.${rs.tPrefix}_hero2`)}</span>
              </h1>

              <p className="text-lg text-muted-foreground mb-6">
                {t(`rootserver.${rs.tPrefix}_desc`)}
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: Shield,     text: 'DDoS Protection' },
                  { icon: Zap,        text: 'Full Root Access' },
                  { icon: CreditCard, text: t('rootserver.prepaid') },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2 px-3 py-1.5 rounded-full glass text-sm">
                    <Icon className="w-4 h-4 text-primary" />{text}
                  </div>
                ))}
              </div>

              <div className="flex items-baseline gap-1">
                <span className="text-muted-foreground">{t('rootserver.from')}</span>
                <span className="text-4xl font-black text-primary ml-2">€{rs.fromPrice}</span>
                <span className="text-muted-foreground">{t('common.perMonth')}</span>
              </div>
            </motion.div>

            {/* Penguin */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex justify-center"
            >
              <img
                src={rs.penguinImage}
                alt={rs.penguinAlt}
                className="w-56 md:w-72 drop-shadow-2xl"
              />
            </motion.div>
          </div>

          {/* ── Plans ─────────────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-20"
          >
            <div className={`grid grid-cols-1 ${cols} gap-6`}>
              {plansWithIdx.map((plan) => (
                <PlanCard key={plan.name} plan={plan} rs={rs} t={t} />
              ))}
            </div>
          </motion.div>

          {/* ── Features ─────────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-8">{t('rootserver.everythingIncluded')}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featureNums.map((n) => {
                const Icon = featureIcons[(n - 1) % featureIcons.length];
                return (
                  <motion.div
                    key={n}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + n * 0.05 }}
                    className="flex gap-4 p-5 glass rounded-xl border border-border hover:border-primary/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-1">
                        {t(`rootserver.${rs.tPrefix}_feat${n}`)}
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {t(`rootserver.${rs.tPrefix}_feat${n}_desc`)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* ── Other type ─────────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-2xl p-8 mb-8"
          >
            <span className="text-sm text-muted-foreground">{t('rootserver.otherType')} </span>
            {ROOTSERVER_TYPES.filter((r) => r.slug !== rs.slug).map((r) => (
              <Link key={r.slug} to={`/rootserver/${r.slug}`} className="ml-2">
                <Button variant="outline" size="sm" className="border-primary/30 hover:border-primary">
                  {t(`rootserver.${r.tPrefix}_title`)}
                  <span className="ml-2 text-xs text-primary">{t('rootserver.from')} €{r.fromPrice}</span>
                </Button>
              </Link>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-3">{t('rootserver.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{t('rootserver.ctaDesc')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/dedicated">
                <Button className="bg-primary hover:bg-primary/90">{t('rootserver.viewDedicated')}</Button>
              </Link>
              <a href="https://deinserverhost.de/dedicated-sonderposten" target="_blank" rel="noopener noreferrer">
                <Button variant="outline">{t('rootserver.viewSale')}</Button>
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
